import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { buildAgentCaseCreatePayload, extractPhoneDigits, mapCaseRowToPanelPhone, mapCrmPhoneToPanelPhone, mapPanelPhoneToCrmPayload, validatePhoneProfileForCreate, } from '@/shared/services/agentPanelPhone.mapper';
import { buildKasePatchBody, mapKaseDtoList, mapKaseDtoToPanelCase, mapKaseDtoToTicketDetail, } from '@/shared/services/kase.mapper';
import { fetchCasePanelReference } from '@/shared/services/casePanelReference';
import { fetchLatestConnectQueueLog, resolveAgentQueueLogName } from '@/shared/services/queueLogApi';
import { AGENT_PANEL_IVR_TRACKS_PAGE_SIZE, mapCdrToIvrTrack, } from '../model/cdr.model';
import { appendCriteriaEquals, appendPageable, readPagedRows, } from '../utils/spring-api';
import { serializeAxiosError } from './reducer.utils';
function api(base) {
    return {
        reference: `${API_ROUTES.issues}`,
        phoneLookup: `${base}`,
        phone: `${base}`,
        cases: base,
        recentCases: base,
        ticketHistory: base,
        ticketDetail: (id) => `${base}/${id}`,
        attachment: (id) => `${base}/${id}/attachments`,
        coreBankingSearch: `${base}`,
    };
}
export function createCasePanelThunks(base) {
    const urls = api(base);
    return {
        getReference: createAsyncThunk(`${base}/reference`, async () => fetchCasePanelReference()),
        phoneLookup: createAsyncThunk(`${base}/phone-lookup`, async (number) => {
            const res = await axios.get(urls.phoneLookup, {
                params: { 'phoneNumber.equals': number, page: 0, size: 1 },
            });
            const { rows } = readPagedRows(res.data, res.headers);
            const result = {
                phone: rows[0]
                    ? {
                        id: rows[0].customerId,
                        number: rows[0].phoneNumber ?? number,
                        name: rows[0].callerName,
                        email: rows[0].customerEmail,
                    }
                    : { number },
                recentCases: rows,
            };
            return result;
        }, { serializeError: serializeAxiosError }),
        updatePhone: createAsyncThunk(`${base}/update-phone`, async (phone) => {
            const res = await axios.put(urls.phone, phone);
            return res.data;
        }, { serializeError: serializeAxiosError }),
        createCase: createAsyncThunk(`${base}/create-case`, async (payload) => {
            const res = await axios.post(urls.cases, payload);
            return res.data;
        }, { serializeError: serializeAxiosError }),
        getRecentCases: createAsyncThunk(`${base}/recent-cases`, async (phoneNumber) => {
            const res = await axios.get(urls.recentCases, {
                params: { 'phoneNumber.equals': phoneNumber, page: 0, size: 20, sort: 'id,desc' },
            });
            return readPagedRows(res.data, res.headers).rows;
        }),
        uploadAttachment: createAsyncThunk(`${base}/attachment`, async ({ caseId, file }) => {
            const formData = new FormData();
            formData.append('file', file, file.name);
            const res = await axios.post(urls.attachment(caseId), formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return { caseId, message: res.data.message };
        }, { serializeError: serializeAxiosError }),
        getTicketHistory: createAsyncThunk(`${base}/ticket-history`, async ({ page, size }) => {
            const res = await axios.get(urls.ticketHistory, {
                params: { page, size, sort: 'id,desc' },
            });
            return { data: res.data, total: Number(res.headers['x-total-count'] || res.data.length) };
        }),
        getTicketDetail: createAsyncThunk(`${base}/ticket-detail`, async (caseId) => {
            const res = await axios.get(urls.ticketDetail(caseId));
            return mapKaseDtoToTicketDetail(res.data);
        }),
        getTicketAssignments: createAsyncThunk(`${API_ROUTES.caseAssignments}/ticket-assignments`, async (caseId) => fetchCaseAssignmentsForCase(caseId), { serializeError: serializeAxiosError }),
        searchCoreBanking: createAsyncThunk(`${base}/core-banking`, async (keyword) => {
            const res = await axios.get(urls.coreBankingSearch, { params: { keyword } });
            return normalizeCoreBankingSearchResponse(res.data);
        }),
    };
}
function normalizeCoreBankingSearchResponse(data) {
    if (data && typeof data === 'object' && !Array.isArray(data) && 'accounts' in data) {
        const payload = data;
        return {
            accounts: payload.accounts ?? [],
            selected: payload.selected,
            errorMessage: payload.errorMessage,
            phone: payload.phone,
            recentCases: payload.recentCases,
        };
    }
    const { rows } = readPagedRows(data);
    return {
        accounts: [],
        recentCases: rows,
        errorMessage: rows.length > 0 ? undefined : 'No records found for this search.',
    };
}
function caseAssignmentsQuery(caseId) {
    const q = new URLSearchParams();
    appendPageable(q, 0, 1000);
    appendCriteriaEquals(q, 'kaseId', String(caseId));
    appendCriteriaEquals(q, 'active', true);
    return q.toString();
}
async function fetchCaseAssignmentsForCase(caseId) {
    const res = await axios.get(`${API_ROUTES.caseAssignments}?${caseAssignmentsQuery(caseId)}`);
    const { rows } = readPagedRows(res.data, res.headers);
    return rows;
}
const RECENT_CASES_PAGE_SIZE = 20;
async function updateKaseWithEditableFields(payload) {
    const patchRes = await axios.patch(`${API_ROUTES.kases}/${payload.caseId}`, buildKasePatchBody(payload));
    return mapKaseDtoToPanelCase(patchRes.data);
}
function mergeUpdatedCaseInList(cases, updated) {
    const id = String(updated.id);
    return cases.map((row) => (String(row.id) === id ? { ...row, ...updated } : row));
}
async function fetchRecentCases(page, size, phoneNumber) {
    const q = new URLSearchParams();
    appendPageable(q, page, size, 'id,desc');
    appendCriteriaEquals(q, 'phoneNumber', phoneNumber?.trim());
    const res = await axios.get(API_ROUTES.kases, { params: q });
    const { rows, total } = readPagedRows(res.data, res.headers);
    return { rows: mapKaseDtoList(rows), total };
}
async function savePhoneToCrm(phone) {
    const payload = mapPanelPhoneToCrmPayload(phone);
    const res = phone.id != null && phone.id !== ''
        ? await axios.put(`${API_ROUTES.phones}/${phone.id}`, payload)
        : await axios.post(API_ROUTES.phones, payload);
    return mapCrmPhoneToPanelPhone(res.data, phone.number);
}
/** `GET /api/phones?page=0&size=1&number.equals={number}` */
async function fetchCrmPhoneByNumber(number) {
    const trimmed = number.trim();
    if (!trimmed)
        return null;
    const q = new URLSearchParams();
    appendPageable(q, 0, 1);
    appendCriteriaEquals(q, 'number', trimmed);
    const res = await axios.get(API_ROUTES.phones, { params: q });
    const { rows } = readPagedRows(res.data, res.headers);
    return rows[0] ?? null;
}
/**
 * Before case create: resolve CRM phone by number; POST customer details only when absent.
 */
async function ensurePhoneProfileForCaseCreate(phone) {
    const trimmed = phone.number?.trim();
    if (!trimmed) {
        throw new Error('Caller ID is required');
    }
    const existing = await fetchCrmPhoneByNumber(trimmed);
    if (existing) {
        return { phone: mapCrmPhoneToPanelPhone(existing, trimmed), profileCreated: false };
    }
    const validationError = validatePhoneProfileForCreate(phone);
    if (validationError) {
        throw new Error(validationError);
    }
    const saved = await savePhoneToCrm(phone);
    return { phone: saved, profileCreated: true };
}
async function lookupPhoneByNumber(number) {
    const trimmed = number.trim();
    const crm = trimmed ? await fetchCrmPhoneByNumber(trimmed) : null;
    let recentCases = [];
    if (trimmed) {
        const cases = await fetchRecentCases(0, RECENT_CASES_PAGE_SIZE, trimmed);
        recentCases = cases.rows;
    }
    if (!crm) {
        return {
            phone: { number: trimmed },
            recentCases,
            message: 'No customer record found for this number.',
            severity: 'warn',
            notFound: true,
        };
    }
    const phone = mapCrmPhoneToPanelPhone(crm, trimmed);
    return {
        phone,
        recentCases,
        customerGroup: phone.customerGroup,
    };
}
const baseCasesPanelThunks = createCasePanelThunks(API_ROUTES.kases);
const casesPanelThunks = {
    ...baseCasesPanelThunks,
    phoneLookup: createAsyncThunk(`${API_ROUTES.phones}/cases-phone-lookup`, async (number) => lookupPhoneByNumber(number), { serializeError: serializeAxiosError }),
    updatePhone: createAsyncThunk(`${API_ROUTES.phones}/cases-save-phone`, async (phone) => {
        const savedPhone = await savePhoneToCrm(phone);
        return {
            phone: savedPhone,
            message: phone.id ? 'Customer updated' : 'Customer created',
        };
    }, { serializeError: serializeAxiosError }),
    createCase: createAsyncThunk(`${API_ROUTES.kases}/cases-create-case`, async (payload, { getState }) => {
        const state = getState();
        const reference = state.casesPanel.reference;
        const { phone, profileCreated } = await ensurePhoneProfileForCaseCreate(payload.phone);
        const body = buildAgentCaseCreatePayload(phone, payload.case, reference);
        const res = await axios.post(API_ROUTES.kases, body);
        let recentCases = [];
        if (phone.number?.trim()) {
            const cases = await fetchRecentCases(0, RECENT_CASES_PAGE_SIZE, phone.number);
            recentCases = cases.rows;
        }
        return {
            case: mapKaseDtoToPanelCase(res.data),
            recentCases,
            phone,
            message: profileCreated
                ? 'Customer profile created and case saved'
                : 'Case created successfully',
        };
    }, { serializeError: serializeAxiosError }),
    getRecentCases: createAsyncThunk(`${API_ROUTES.kases}/cases-recent-cases`, async (phoneNumber) => {
        const { rows } = await fetchRecentCases(0, RECENT_CASES_PAGE_SIZE, phoneNumber);
        return rows;
    }),
    updateCase: createAsyncThunk(`${API_ROUTES.kases}/cases-update-case`, async (payload) => updateKaseWithEditableFields(payload), { serializeError: serializeAxiosError }),
};
const agentPanelThunks = {
    getReference: casesPanelThunks.getReference,
    phoneLookup: createAsyncThunk(`${API_ROUTES.kases}/agent-phone-lookup`, async (number) => lookupPhoneByNumber(number), { serializeError: serializeAxiosError }),
    updatePhone: createAsyncThunk(`${API_ROUTES.phones}/agent-save-phone`, async (phone) => {
        const savedPhone = await savePhoneToCrm(phone);
        return {
            phone: savedPhone,
            message: phone.id ? 'Customer updated' : 'Customer created',
        };
    }, { serializeError: serializeAxiosError }),
    fetchInboundQueueLog: createAsyncThunk(`${API_ROUTES.queueLogsLatest}/agent-inbound`, async (callerDigits, { getState }) => {
        const agentName = resolveAgentQueueLogName(getState().auth.crmProfile);
        const queueLog = agentName ? await fetchLatestConnectQueueLog(agentName) : null;
        return { queueLog, callerDigits: callerDigits.trim() };
    }, { serializeError: serializeAxiosError }),
    createCase: createAsyncThunk(`${API_ROUTES.kases}/agent-create-case`, async (payload, { getState }) => {
        const state = getState();
        const reference = state.agentPanel.reference;
        const { phone, profileCreated } = await ensurePhoneProfileForCaseCreate(payload.phone);
        const phoneDigits = extractPhoneDigits(phone.number ?? '');
        const linkedCaller = state.agentPanel.inboundQueueLogForNumber?.trim() ?? '';
        const queueLog = phoneDigits && linkedCaller && phoneDigits === extractPhoneDigits(linkedCaller)
            ? state.agentPanel.inboundQueueLog
            : null;
        const sessionKaseId = state.agentPanel.inboundCallSessionKaseId;
        const sessionUniqueId = state.agentPanel.inboundCallSessionUniqueId?.trim() ?? '';
        const callLink = sessionKaseId != null && Number.isFinite(sessionKaseId) && sessionUniqueId
            ? { kaseId: sessionKaseId, refUniqueId: sessionUniqueId }
            : undefined;
        const body = buildAgentCaseCreatePayload(phone, payload.case, reference, callLink ? null : queueLog, callLink);
        const res = await axios.post(API_ROUTES.kases, body);
        let recentCases = [];
        if (phone.number?.trim()) {
            const cases = await fetchRecentCases(0, RECENT_CASES_PAGE_SIZE, phone.number);
            recentCases = cases.rows;
        }
        const mappedCase = mapKaseDtoToPanelCase(res.data);
        const caseId = mappedCase?.id != null ? Number(mappedCase.id) : NaN;
        const uniqueId = mappedCase?.uniqueId?.trim() ||
            mappedCase?.callid?.trim() ||
            (!callLink ? queueLog?.callid?.trim() : '') ||
            '';
        return {
            case: mappedCase,
            recentCases,
            phone,
            message: profileCreated
                ? 'Customer profile created and case saved'
                : 'Case created successfully',
            inboundCallSession: Number.isFinite(caseId) && caseId > 0 && uniqueId
                ? { kaseId: caseId, uniqueId }
                : undefined,
        };
    }, { serializeError: serializeAxiosError }),
    getRecentCases: createAsyncThunk(`${API_ROUTES.kases}/agent-recent-cases`, async (phoneNumber) => {
        const { rows } = await fetchRecentCases(0, RECENT_CASES_PAGE_SIZE, phoneNumber);
        return rows;
    }),
    uploadAttachment: casesPanelThunks.uploadAttachment,
    getTicketHistory: createAsyncThunk(`${API_ROUTES.kases}/agent-ticket-history`, async ({ phoneNumber, page = 0, size = 3, }, { getState }) => {
        const activeNumber = phoneNumber?.trim() || getState().agentPanel.phone.number?.trim() || '';
        if (!activeNumber) {
            return { data: [], total: 0 };
        }
        const { rows, total } = await fetchRecentCases(page, size, activeNumber);
        return { data: rows, total };
    }),
    getTicketDetail: casesPanelThunks.getTicketDetail,
    getTicketAssignments: casesPanelThunks.getTicketAssignments,
    searchCoreBanking: createAsyncThunk(`${API_ROUTES.kases}/agent-customer-search`, async (keyword) => {
        const trimmed = keyword.trim();
        const crm = trimmed ? await fetchCrmPhoneByNumber(trimmed) : null;
        const { rows: caseRows } = await fetchRecentCases(0, RECENT_CASES_PAGE_SIZE, trimmed);
        const phone = crm
            ? mapCrmPhoneToPanelPhone(crm, trimmed)
            : caseRows[0]
                ? mapCaseRowToPanelPhone(caseRows[0], trimmed)
                : undefined;
        const result = {
            accounts: [],
            recentCases: caseRows,
            phone,
            errorMessage: !phone && caseRows.length === 0
                ? 'No customer or case records found for this search.'
                : undefined,
        };
        return result;
    }, { serializeError: serializeAxiosError }),
    updateCase: casesPanelThunks.updateCase,
    getIvrTracksBySrc: createAsyncThunk(`${API_ROUTES.cdrs}/agent-ivr-tracks`, async (src) => {
        const digits = src.replace(/\D/g, '');
        if (!digits) {
            return [];
        }
        const q = new URLSearchParams();
        appendPageable(q, 0, AGENT_PANEL_IVR_TRACKS_PAGE_SIZE, 'start,desc');
        appendCriteriaEquals(q, 'src', digits);
        const res = await axios.get(API_ROUTES.cdrs, { params: q });
        const { rows } = readPagedRows(res.data, res.headers);
        return rows.map(mapCdrToIvrTrack);
    }, { serializeError: serializeAxiosError }),
};
const panelInitialState = {
    reference: null,
    referenceLoading: false,
    phone: { number: '' },
    /** Caller number from the active inbound SIP ring (set globally by AgentInboundCallBridge). */
    inboundCallingNumber: '',
    inboundLastCalledNumber: '',
    /** Latest CONNECT queue log fetched on inbound ring, paired with `inboundQueueLogForNumber`. */
    inboundQueueLog: null,
    inboundQueueLogForNumber: '',
    /** First case in the active inbound call — used to link additional cases via `kaseId` / `refUniqueId`. */
    inboundCallSessionKaseId: null,
    inboundCallSessionUniqueId: '',
    inboundQueueLogLoading: false,
    lookupNotFound: false,
    recentCases: [],
    recentCasesLoading: false,
    lookupLoading: false,
    savingPhone: false,
    creatingCase: false,
    updatingCase: false,
    errorMessage: null,
    ticketHistory: [],
    ticketHistoryTotal: 0,
    ticketHistoryLoading: false,
    selectedTicket: null,
    ticketDetailLoading: false,
    ticketAssignments: [],
    ticketAssignmentsLoading: false,
    coreBankingAccounts: [],
    selectedAccount: null,
    coreBankingLoading: false,
    coreBankingError: null,
    ivrTracks: [],
    ivrTracksLoading: false,
};
function buildExtraReducers(builder, thunks, options) {
    builder
        .addCase(thunks.getReference.pending, (state) => {
        state.referenceLoading = true;
    })
        .addCase(thunks.getReference.fulfilled, (state, action) => {
        state.referenceLoading = false;
        state.reference = action.payload;
    })
        .addCase(thunks.getReference.rejected, (state) => {
        state.referenceLoading = false;
    })
        .addCase(thunks.phoneLookup.pending, (state) => {
        state.lookupLoading = true;
        state.errorMessage = null;
        state.lookupNotFound = false;
    })
        .addCase(thunks.phoneLookup.fulfilled, (state, action) => {
        state.lookupLoading = false;
        state.phone = action.payload.phone;
        state.recentCases = action.payload.recentCases;
        state.lookupNotFound = Boolean(action.payload.notFound);
        if (options?.syncTicketHistoryOnLookup) {
            state.ticketHistory = action.payload.recentCases;
            state.ticketHistoryTotal = action.payload.recentCases.length;
        }
    })
        .addCase(thunks.phoneLookup.rejected, (state, action) => {
        state.lookupLoading = false;
        state.errorMessage = action.error?.message ?? 'Phone lookup failed';
    })
        .addCase(thunks.updatePhone.pending, (state) => {
        state.savingPhone = true;
    })
        .addCase(thunks.updatePhone.fulfilled, (state, action) => {
        state.savingPhone = false;
        state.phone = action.payload.phone;
        state.lookupNotFound = false;
    })
        .addCase(thunks.updatePhone.rejected, (state, action) => {
        state.savingPhone = false;
        state.errorMessage = action.payload ?? 'Failed to save customer';
    })
        .addCase(thunks.createCase.pending, (state) => {
        state.creatingCase = true;
    })
        .addCase(thunks.createCase.fulfilled, (state, action) => {
        state.creatingCase = false;
        state.recentCases = action.payload.recentCases;
        if ('phone' in action.payload && action.payload.phone) {
            state.phone = action.payload.phone;
            if (action.payload.phone.id != null && action.payload.phone.id !== '') {
                state.lookupNotFound = false;
            }
        }
        if (options?.syncTicketHistoryOnLookup) {
            state.ticketHistory = action.payload.recentCases;
            state.ticketHistoryTotal = action.payload.recentCases.length;
        }
        if (options?.trackInboundCallSessionOnCreate) {
            const session = action.payload
                .inboundCallSession;
            if (session) {
                state.inboundCallSessionKaseId = session.kaseId;
                state.inboundCallSessionUniqueId = session.uniqueId;
            }
        }
    })
        .addCase(thunks.createCase.rejected, (state) => {
        state.creatingCase = false;
    })
        .addCase(thunks.getRecentCases.pending, (state) => {
        state.recentCasesLoading = true;
    })
        .addCase(thunks.getRecentCases.fulfilled, (state, action) => {
        state.recentCasesLoading = false;
        state.recentCases = action.payload;
    })
        .addCase(thunks.getRecentCases.rejected, (state) => {
        state.recentCasesLoading = false;
    })
        .addCase(thunks.updateCase.pending, (state) => {
        state.updatingCase = true;
    })
        .addCase(thunks.updateCase.fulfilled, (state, action) => {
        state.updatingCase = false;
        const updated = action.payload;
        state.recentCases = mergeUpdatedCaseInList(state.recentCases, updated);
        state.ticketHistory = mergeUpdatedCaseInList(state.ticketHistory, updated);
        if (state.selectedTicket && String(state.selectedTicket.id) === String(updated.id)) {
            state.selectedTicket = { ...state.selectedTicket, ...updated };
        }
    })
        .addCase(thunks.updateCase.rejected, (state) => {
        state.updatingCase = false;
    })
        .addCase(thunks.getTicketHistory.pending, (state) => {
        state.ticketHistoryLoading = true;
    })
        .addCase(thunks.getTicketHistory.fulfilled, (state, action) => {
        state.ticketHistoryLoading = false;
        state.ticketHistory = action.payload.data;
        state.ticketHistoryTotal = action.payload.total;
    })
        .addCase(thunks.getTicketHistory.rejected, (state) => {
        state.ticketHistoryLoading = false;
    })
        .addCase(thunks.getTicketDetail.pending, (state) => {
        state.ticketDetailLoading = true;
    })
        .addCase(thunks.getTicketDetail.fulfilled, (state, action) => {
        state.ticketDetailLoading = false;
        state.selectedTicket = action.payload;
    })
        .addCase(thunks.getTicketDetail.rejected, (state) => {
        state.ticketDetailLoading = false;
    })
        .addCase(thunks.getTicketAssignments.pending, (state) => {
        state.ticketAssignmentsLoading = true;
        state.ticketAssignments = [];
    })
        .addCase(thunks.getTicketAssignments.fulfilled, (state, action) => {
        state.ticketAssignmentsLoading = false;
        state.ticketAssignments = action.payload;
    })
        .addCase(thunks.getTicketAssignments.rejected, (state) => {
        state.ticketAssignmentsLoading = false;
        state.ticketAssignments = [];
    })
        .addCase(thunks.searchCoreBanking.pending, (state) => {
        state.coreBankingLoading = true;
        state.coreBankingError = null;
    })
        .addCase(thunks.searchCoreBanking.fulfilled, (state, action) => {
        state.coreBankingLoading = false;
        const payload = action.payload;
        const accounts = payload.accounts ?? [];
        state.coreBankingAccounts = accounts;
        state.selectedAccount = payload.selected ?? accounts[0] ?? null;
        state.coreBankingError = payload.errorMessage ?? null;
        if (payload.phone) {
            state.phone = payload.phone;
        }
        if (payload.recentCases) {
            state.recentCases = payload.recentCases;
            if (options?.syncTicketHistoryOnLookup) {
                state.ticketHistory = payload.recentCases;
                state.ticketHistoryTotal = payload.recentCases.length;
            }
        }
    })
        .addCase(thunks.searchCoreBanking.rejected, (state) => {
        state.coreBankingLoading = false;
        state.coreBankingError = 'Core banking search failed';
    });
}
export const casesPanelSlice = createSlice({
    name: 'casesPanel',
    initialState: panelInitialState,
    reducers: {
        resetCasesPanel() {
            return panelInitialState;
        },
        setCasesPanelPhone(state, action) {
            state.phone = action.payload;
        },
        clearCasesPanelTicket(state) {
            state.selectedTicket = null;
            state.ticketAssignments = [];
            state.ticketAssignmentsLoading = false;
        },
        clearCoreBanking(state) {
            state.coreBankingAccounts = [];
            state.selectedAccount = null;
            state.coreBankingError = null;
        },
        selectCoreBankingAccount(state, action) {
            state.selectedAccount = action.payload;
        },
    },
    extraReducers: (builder) => buildExtraReducers(builder, casesPanelThunks),
});
export const agentPanelSlice = createSlice({
    name: 'agentPanel',
    initialState: panelInitialState,
    reducers: {
        resetAgentPanel() {
            return panelInitialState;
        },
        setAgentPanelPhone(state, action) {
            state.phone = action.payload;
        },
        setInboundCallingNumber(state, action) {
            const next = action.payload.trim();
            if (next !== state.inboundCallingNumber) {
                if (state.inboundCallingNumber && state.inboundCallingNumber !== next) {
                    state.inboundLastCalledNumber = state.inboundCallingNumber;
                }
                state.inboundCallSessionKaseId = null;
                state.inboundCallSessionUniqueId = '';
            }
            state.inboundCallingNumber = next;
        },
        clearInboundCallContext(state) {
            state.inboundCallingNumber = '';
            state.inboundLastCalledNumber = '';
            state.inboundQueueLog = null;
            state.inboundQueueLogForNumber = '';
            state.inboundQueueLogLoading = false;
            state.inboundCallSessionKaseId = null;
            state.inboundCallSessionUniqueId = '';
            state.lookupNotFound = false;
        },
        clearAgentPanelTicket(state) {
            state.selectedTicket = null;
            state.ticketAssignments = [];
            state.ticketAssignmentsLoading = false;
        },
        clearAgentCoreBanking(state) {
            state.coreBankingAccounts = [];
            state.selectedAccount = null;
            state.coreBankingError = null;
        },
        selectAgentCoreBankingAccount(state, action) {
            state.selectedAccount = action.payload;
        },
    },
    extraReducers: (builder) => {
        buildExtraReducers(builder, agentPanelThunks, {
            syncTicketHistoryOnLookup: true,
            trackInboundCallSessionOnCreate: true,
        });
        builder
            .addCase(agentPanelThunks.getIvrTracksBySrc.pending, (state) => {
            state.ivrTracksLoading = true;
        })
            .addCase(agentPanelThunks.getIvrTracksBySrc.fulfilled, (state, action) => {
            state.ivrTracksLoading = false;
            state.ivrTracks = action.payload;
        })
            .addCase(agentPanelThunks.getIvrTracksBySrc.rejected, (state) => {
            state.ivrTracksLoading = false;
            state.ivrTracks = [];
        })
            .addCase(agentPanelThunks.fetchInboundQueueLog.pending, (state) => {
            state.inboundQueueLogLoading = true;
        })
            .addCase(agentPanelThunks.fetchInboundQueueLog.fulfilled, (state, action) => {
            state.inboundQueueLogLoading = false;
            state.inboundQueueLog = action.payload.queueLog;
            state.inboundQueueLogForNumber = action.payload.callerDigits;
        })
            .addCase(agentPanelThunks.fetchInboundQueueLog.rejected, (state) => {
            state.inboundQueueLogLoading = false;
            state.inboundQueueLog = null;
            state.inboundQueueLogForNumber = '';
        });
    },
});
export const casesPanelReducer = casesPanelSlice.reducer;
export const agentPanelReducer = agentPanelSlice.reducer;
export const casesPanelActions = {
    ...casesPanelThunks,
    ...casesPanelSlice.actions,
};
export const agentPanelActions = {
    ...agentPanelThunks,
    ...agentPanelSlice.actions,
};
